import { Fragment, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { ADMIN_NAV } from '../lib/admin-nav';

interface AdminLayoutProps {
  /** When true, the content area uses the full-bleed `mh-admin-fluid` wrapper. */
  fluid?: boolean;
  children: ReactNode;
}

/**
 * Two-pane layout for back-office (`/admin/*`) pages.
 *
 * - Left: persistent sidebar nav grouped into Activity / Configuration /
 *   Operations.
 * - Right: page content (the page provides its own `<h1>` and body).
 *
 * Replaces the old `<SectionTabs items={OPS_TABS}>` wrapper. Top-level
 * public sections (Events, Venues, Series, Submit) still use SectionTabs.
 */
export default function AdminLayout({ fluid = false, children }: AdminLayoutProps) {
  return (
    <div className={`mh-admin-layout${fluid ? ' mh-admin-layout--fluid' : ''}`}>
      <aside className="mh-admin-layout__sidebar" aria-label="Admin sections">
        <nav>
          <ul className="mh-admin-nav nav nav-pills">
            {ADMIN_NAV.map((item) => (
              <Fragment key={item.to}>
                {item.groupStart && (
                  <li
                    className="mh-admin-nav__group-label small text-uppercase text-body-secondary fw-semibold px-2 pt-3 pb-1"
                    aria-hidden="true"
                  >
                    {item.groupStart}
                  </li>
                )}
                <li className="nav-item">
                  <NavLink
                    to={item.to}
                    className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                  >
                    {item.icon && (
                      <i className={`bi bi-${item.icon} mh-admin-nav__icon`} aria-hidden="true" />
                    )}
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              </Fragment>
            ))}
          </ul>
        </nav>
      </aside>
      <div className="mh-admin-layout__content">
        {children}
      </div>
    </div>
  );
}
